import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, FileDown, Utensils } from "lucide-react";
import { toast } from "sonner";
import { useActiveSchool } from "@/lib/activeSchool";
import { dailyApi } from "@/lib/setupApi";
import { buildDayBreakdown } from "@/lib/mealPlan";
import { isMealDay } from "@/lib/dayKind";
import MobileDailyConsumption from "@/components/poshan/MobileDailyConsumption";

// Daily Consumption Report — class-wise day-wise ledger of meals served and
// every ingredient consumed (qty + Rs). Matches the deterministic per-day
// meal-plan math (the same source feeding Fund Management consumed and
// Annexure-1 grand totals — guaranteed to reconcile).

const CLASS_OPTIONS = [
  { value: "all",          label: "All Classes" },
  { value: "balVatika",    label: "Bal Vatika" },
  { value: "primary",      label: "Primary (I-V)" },
  { value: "upperPrimary", label: "Upper Primary (VI-VIII)" },
  { value: "highSchool",   label: "High School (IX-X)" },
];

const MONTHS = [
  { v: 1, label: "January" }, { v: 2, label: "February" }, { v: 3, label: "March" },
  { v: 4, label: "April" },   { v: 5, label: "May" },      { v: 6, label: "June" },
  { v: 7, label: "July" },    { v: 8, label: "August" },   { v: 9, label: "September" },
  { v: 10, label: "October" }, { v: 11, label: "November" }, { v: 12, label: "December" },
];

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Stable column ordering — lines that don't appear on a given day render as "—".
// Two ladoo-only items (Sesame, Cardamom) are folded into "Others" to keep the
// table narrow; full breakdown is still available in the daily JSON if needed.
// Rice is free (FCI supply) — quantity-only column, no Rs sub-column.
const ITEM_ORDER = [
  "Rice", "Dal", "Soya", "Egg", "Vegetable",
  "Oil", "Refined Oil",
  "Ragi Flour", "Wheat Flour", "Sugar", "Ground Nuts", "Sesame", "Cardamom",
  "Masala & Salt", "Fuel",
];
const QTY_ONLY_ITEMS = new Set(["Rice"]);
const LADOO_PIECE_ITEM = "Ladoo Making Cost";

// Numeric formatters
const num3 = (n) => Number(n || 0).toFixed(3);
const num2 = (n) => Number(n || 0).toFixed(2);
// `Rs\u00A0` uses a non-breaking space so "Rs 412.55" renders as one unbreakable
// token even if a parent cell allowed wrapping.
const rs = (n) => `Rs\u00A0${num2(n)}`;

function ymd(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// `2026-02-05` → `05-02-2026` (Indian dd-mm-yyyy printed format).
function formatDateDmy(iso) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}

// Aggregate per-day per-class consumption.
// Returns one row per saved daily entry filtered to selected class.
function buildDayRows(entries, classKey) {
  return entries.map((entry) => {
    const att = entry.attendance || {};
    const breakdown = buildDayBreakdown(entry.date, att);
    if (!breakdown) return null;

    let mealsServed = 0;
    const items = new Map(); // item → { qty, amount, unit }
    let ladooPieces = 0;
    let ladooMakingCharge = 0;

    if (classKey === "all") {
      // Use breakdown.aggregate (sum across all classes) — deterministic,
      // matches Fund-Management consumed exactly.
      mealsServed = (breakdown.perClass || []).reduce((s, c) => s + (c.count || 0), 0);
      (breakdown.aggregate || []).forEach((row) => {
        items.set(row.name, { qty: row.totalQty || 0, amount: row.totalAmount || 0, unit: row.unit || "kg" });
        if (row.name === LADOO_PIECE_ITEM) {
          ladooPieces = row.totalQty || 0;
          ladooMakingCharge = row.totalAmount || 0;
        }
      });
    } else {
      // Specific class: take that class's `lines` (per-student) × `count`.
      const cls = (breakdown.perClass || []).find((c) => c.key === classKey);
      if (!cls) return null;
      mealsServed = cls.count || 0;
      (cls.lines || []).forEach((l) => {
        const qPerStudent = l.costMode === "flat" ? 0 : Number(l.qty || 0);
        const qty = qPerStudent * mealsServed;
        const amount = Number(l.costPerStudent || 0) * mealsServed;
        const cur = items.get(l.item) || { qty: 0, amount: 0, unit: l.unit || "kg" };
        cur.qty += qty; cur.amount += amount;
        items.set(l.item, cur);
        if (l.item === LADOO_PIECE_ITEM) {
          ladooPieces += qty;             // qty here = pieces (qPerStudent=1 × students)
          ladooMakingCharge += amount;
        }
      });
    }

    const dayCost = Array.from(items.entries()).reduce((s, [k, v]) => k === LADOO_PIECE_ITEM ? s : s + v.amount, 0) + ladooMakingCharge;
    const d = new Date(entry.date + "T00:00:00");
    return {
      date: entry.date,
      day: DAY_NAMES[d.getDay()],
      mealName: breakdown.mealName || "—",
      mealsServed,
      items,
      ladooPieces,
      ladooMakingCharge,
      dayCost,
      entryType: entry.entryType,
    };
  }).filter(Boolean);
}

// Class-wise rice OPENING stock for the displayed (year, month). We read the
// closing of the previous month from `fund_balances` (cascaded). Each row's
// "Rice Stock" then = stock_so_far + opening - cumulative_consumed_through_day.
async function loadOpeningRice(schoolId, year, month, classKey) {
  if (!schoolId) return 0;
  const { cascadeOpening } = await import("@/lib/fundCascade");
  const prev = await cascadeOpening(schoolId, year, month).catch(() => null);
  if (!prev) return 0;
  if (classKey === "all") {
    return Object.values(prev.rice || {}).reduce((s, v) => s + (v || 0), 0);
  }
  return Number(prev.rice?.[classKey] || 0);
}

export default function DailyConsumption() {
  const { active } = useActiveSchool();
  const schoolId = active?.id || "";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [classKey, setClassKey] = useState("all");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingRice, setOpeningRice] = useState(0);

  const yearChoices = useMemo(() => {
    const cy = today.getFullYear();
    return [cy - 2, cy - 1, cy, cy + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load daily entries for the selected month.
  useEffect(() => {
    if (!schoolId) { setEntries([]); return; }
    let cancelled = false;
    setLoading(true);
    const last = new Date(year, month, 0).getDate();
    dailyApi.list({
      schoolId,
      fromDate: ymd(year, month, 1),
      toDate: ymd(year, month, last),
    })
      // iter154 — filter out no-MDM / holiday entries so the Daily
      // Consumption table only lists dates where meals were actually
      // served. Non-serving dates contribute 0 rice + 0 cost and would
      // only add visual noise.
      .then((d) => {
        if (cancelled) return;
        const servedOnly = (d || []).filter(isMealDay);
        setEntries(servedOnly.sort((a, b) => a.date.localeCompare(b.date)));
      })
      .catch(() => { if (!cancelled) setEntries([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [schoolId, year, month]);

  // Load previous month's closing rice (becomes this month's opening rice for the running stock column).
  useEffect(() => {
    let cancelled = false;
    loadOpeningRice(schoolId, year, month, classKey)
      .then((v) => { if (!cancelled) setOpeningRice(v); })
      .catch(() => { if (!cancelled) setOpeningRice(0); });
    return () => { cancelled = true; };
  }, [schoolId, year, month, classKey]);

  const rows = useMemo(() => buildDayRows(entries, classKey), [entries, classKey]);

  // Active items list — show columns only for items that actually appeared this month
  // (avoids 14-column wall when the meal pattern only uses 6 of them).
  const activeItems = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => r.items.forEach((v, k) => {
      if (k === LADOO_PIECE_ITEM) return;
      if ((v.amount || 0) > 0 || (v.qty || 0) > 0) seen.add(k);
    }));
    return ITEM_ORDER.filter((it) => seen.has(it));
  }, [rows]);

  // Totals row (across all displayed days).
  const totals = useMemo(() => {
    const t = { mealsServed: 0, ladooPieces: 0, ladooMakingCharge: 0, dayCost: 0 };
    const itemTotals = new Map(); // item → { qty, amount }
    activeItems.forEach((it) => itemTotals.set(it, { qty: 0, amount: 0 }));
    rows.forEach((r) => {
      t.mealsServed += r.mealsServed;
      t.ladooPieces += r.ladooPieces;
      t.ladooMakingCharge += r.ladooMakingCharge;
      t.dayCost += r.dayCost;
      activeItems.forEach((it) => {
        const cur = r.items.get(it);
        if (cur) {
          const tot = itemTotals.get(it);
          tot.qty += cur.qty || 0;
          tot.amount += cur.amount || 0;
        }
      });
    });
    return { ...t, itemTotals };
  }, [rows, activeItems]);

  // Running rice stock per day. Stock starts at openingRice, decreases by each
  // day's rice qty (kg). Rice line uses `unit: "gm"` per-student and we already
  // accumulated qty in gm units inside `items` — convert to kg here.
  const ricePerDayKg = (row) => (row.items.get("Rice")?.qty || 0) / 1000;
  const stockSeries = useMemo(() => {
    let stock = openingRice;
    return rows.map((r) => {
      const used = ricePerDayKg(r);
      const stockAfter = stock - used;
      const o = stock;
      stock = stockAfter;
      return { opening: o, used, closing: stockAfter };
    });
  }, [rows, openingRice]);

  // CSV export
  const downloadCsv = () => {
    if (rows.length === 0) { toast.error("Nothing to export"); return; }
    const itemHeader = (it) => QTY_ONLY_ITEMS.has(it) ? [`${it} Qty`] : [`${it} Qty`, `${it} (Rs)`];
    const header = [
      "Date", "Day", "Served Item", "Meals Served",
      "Rice Stock Opening (kg)", "Rice Used (kg)", "Rice Stock Closing (kg)",
      ...activeItems.flatMap(itemHeader),
      "Ladoo Pieces", "Ladoo Making Charge (Rs)", "Day Cost (Rs)",
    ];
    const lines = [header.join(",")];
    rows.forEach((r, i) => {
      const s = stockSeries[i];
      lines.push([
        formatDateDmy(r.date), r.day, `"${r.mealName}"`, r.mealsServed,
        num3(s.opening), num3(s.used), num3(s.closing),
        ...activeItems.flatMap((it) => {
          const v = r.items.get(it);
          const qtyOnly = QTY_ONLY_ITEMS.has(it);
          if (!v) return qtyOnly ? [""] : ["", ""];
          const qDisplay = it === "Egg" ? Math.round(v.qty || 0) : num3((v.qty || 0) / (["gm", "ml"].includes(v.unit) ? 1000 : 1));
          return qtyOnly ? [qDisplay] : [qDisplay, num2(v.amount || 0)];
        }),
        Math.round(r.ladooPieces || 0), num2(r.ladooMakingCharge), num2(r.dayCost),
      ].join(","));
    });
    // Totals row
    lines.push([
      "TOTAL", "", "", totals.mealsServed,
      num3(openingRice), num3(stockSeries.reduce((s, x) => s + x.used, 0)), num3(stockSeries.length ? stockSeries[stockSeries.length - 1].closing : openingRice),
      ...activeItems.flatMap((it) => {
        const tot = totals.itemTotals.get(it);
        const qtyOnly = QTY_ONLY_ITEMS.has(it);
        const unit = rows.find((r) => r.items.get(it))?.items.get(it)?.unit || "kg";
        const qDisplay = it === "Egg" ? Math.round(tot.qty) : num3(tot.qty / (["gm", "ml"].includes(unit) ? 1000 : 1));
        return qtyOnly ? [qDisplay] : [qDisplay, num2(tot.amount)];
      }),
      Math.round(totals.ladooPieces), num2(totals.ladooMakingCharge), num2(totals.dayCost),
    ].join(","));

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daily-consumption-${year}-${String(month).padStart(2, "0")}-${classKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const monthLabel = MONTHS.find((m) => m.v === month)?.label || month;
  const classLabel = CLASS_OPTIONS.find((c) => c.value === classKey)?.label || "";

  // Number of body td's per row = 4 (Date,Day,Meal,Meals) + 3 (Stock) + sum_of_per_item_cols + 3 (Ladoo×2 + DayCost).
  const ingredientColCount = activeItems.reduce((s, it) => s + (QTY_ONLY_ITEMS.has(it) ? 1 : 2), 0);
  const totalColCount = 4 + 3 + ingredientColCount + 3;

  // Print: tag <body> with a class so our print CSS shows ONLY the report
  // (with school header) in A4 LANDSCAPE; everything else is hidden.
  const doPrint = () => {
    document.body.classList.add("consumption-print-active");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("consumption-print-active"), 200);
    }, 50);
  };

  // Helper: render a complete <table> for a given slice of rows. Used both
  // for the on-screen interactive view (all rows in one table) AND for the
  // two paginated print copies (each one fits one A4 landscape page).
  const renderTable = (rowSlice, sliceOffset, includeTotalsRow) => (
    <table className="w-full text-xs" data-testid={includeTotalsRow ? "consumption-table" : "consumption-table-slice"}>
      <thead className="bg-sand-100 text-ink-900">
        <tr>
          <th rowSpan={2} className="border border-border p-2 text-left align-top">Date</th>
          <th rowSpan={2} className="border border-border p-2 text-left align-top">Day</th>
          <th rowSpan={2} className="border border-border p-2 text-left align-top">Meal</th>
          <th rowSpan={2} className="border border-border p-2 text-center align-top">Meals Served</th>
          <th colSpan={3} className="border border-border p-2 text-center bg-sand-200">Rice Stock (kg)</th>
          {activeItems.map((it) => (
            <th key={it} colSpan={QTY_ONLY_ITEMS.has(it) ? 1 : 2} className="border border-border p-2 text-center">{it}{QTY_ONLY_ITEMS.has(it) ? " (Free)" : ""}</th>
          ))}
          <th colSpan={2} className="border border-border p-2 text-center bg-sand-200">Ladoo</th>
          <th rowSpan={2} className="border border-border p-2 text-center bg-sand-200">Day Cost (Rs)</th>
        </tr>
        <tr>
          <th className="border border-border p-2 text-center text-[10px] font-semibold">Opening</th>
          <th className="border border-border p-2 text-center text-[10px] font-semibold">Used</th>
          <th className="border border-border p-2 text-center text-[10px] font-semibold">Closing</th>
          {activeItems.map((it) => (
            <span key={it} style={{ display: "contents" }}>
              <th className="border border-border p-2 text-center text-[10px] font-semibold">Qty</th>
              {!QTY_ONLY_ITEMS.has(it) && (
                <th className="border border-border p-2 text-center text-[10px] font-semibold">Rs</th>
              )}
            </span>
          ))}
          <th className="border border-border p-2 text-center text-[10px] font-semibold">Pieces</th>
          <th className="border border-border p-2 text-center text-[10px] font-semibold">Charge (Rs)</th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <tr><td colSpan={totalColCount} className="p-6 text-center text-ink-700">Loading…</td></tr>
        )}
        {!loading && rowSlice.length === 0 && includeTotalsRow && (
          <tr><td colSpan={totalColCount} className="p-6 text-center text-ink-700">No daily entries saved for {monthLabel} {year}. Add some on the <b>Daily Entry</b> page.</td></tr>
        )}
        {rowSlice.map((r, idx) => {
          const i = sliceOffset + idx;
          const s = stockSeries[i];
          return (
            <tr key={r.date} className="border-t border-border hover:bg-sand-50" data-testid={`consumption-row-${r.date}`}>
              <td className="border border-border p-2 align-top text-left">{formatDateDmy(r.date)}</td>
              <td className="border border-border p-2 align-top text-left">{r.day}</td>
              <td className="border border-border p-2 align-top text-left">{r.mealName}</td>
              <td className="border border-border p-2 text-center font-semibold">{r.mealsServed}</td>
              <td className="border border-border p-2 text-center tabular-nums">{num3(s.opening)}</td>
              <td className="border border-border p-2 text-center tabular-nums text-clay">{num3(s.used)}</td>
              <td className="border border-border p-2 text-center tabular-nums">{num3(s.closing)}</td>
              {activeItems.map((it) => {
                const v = r.items.get(it);
                const qtyOnly = QTY_ONLY_ITEMS.has(it);
                if (!v || (!v.qty && !v.amount)) {
                  return (
                    <span key={it} style={{ display: "contents" }}>
                      <td className="border border-border p-2 text-center text-ink-700">—</td>
                      {!qtyOnly && (<td className="border border-border p-2 text-center text-ink-700">—</td>)}
                    </span>
                  );
                }
                const isWeight = ["gm", "ml"].includes(v.unit);
                const qDisplay = it === "Egg" ? Math.round(v.qty) : num3(v.qty / (isWeight ? 1000 : 1));
                const qUnit = it === "Egg" ? "\u00A0pcs" : isWeight ? "\u00A0kg" : `\u00A0${v.unit}`;
                return (
                  <span key={it} style={{ display: "contents" }}>
                    <td className="border border-border p-2 text-center tabular-nums">{qDisplay}{qUnit}</td>
                    {!qtyOnly && (<td className="border border-border p-2 text-center tabular-nums">{rs(v.amount)}</td>)}
                  </span>
                );
              })}
              <td className="border border-border p-2 text-center tabular-nums">{r.ladooPieces ? Math.round(r.ladooPieces) : "—"}</td>
              <td className="border border-border p-2 text-center tabular-nums">{r.ladooMakingCharge ? rs(r.ladooMakingCharge) : "—"}</td>
              <td className="border border-border p-2 text-center font-semibold tabular-nums">{rs(r.dayCost)}</td>
            </tr>
          );
        })}
        {includeTotalsRow && rows.length > 0 && (
          <tr className="border-t-2 border-ink-900 bg-sand-100 font-semibold" data-testid="consumption-totals-row">
            <td className="border border-border p-2 text-left" colSpan={3}>TOTAL ({rows.length} day{rows.length === 1 ? "" : "s"})</td>
            <td className="border border-border p-2 text-center">{totals.mealsServed}</td>
            <td className="border border-border p-2 text-center tabular-nums">{num3(openingRice)}</td>
            <td className="border border-border p-2 text-center tabular-nums text-clay">{num3(stockSeries.reduce((s, x) => s + x.used, 0))}</td>
            <td className="border border-border p-2 text-center tabular-nums">{num3(stockSeries.length ? stockSeries[stockSeries.length - 1].closing : openingRice)}</td>
            {activeItems.map((it) => {
              const tot = totals.itemTotals.get(it);
              const qtyOnly = QTY_ONLY_ITEMS.has(it);
              const unit = rows.find((r) => r.items.get(it))?.items.get(it)?.unit || "kg";
              const isWeight = ["gm", "ml"].includes(unit);
              const qDisplay = it === "Egg" ? Math.round(tot.qty) : num3(tot.qty / (isWeight ? 1000 : 1));
              const qUnit = it === "Egg" ? "\u00A0pcs" : isWeight ? "\u00A0kg" : `\u00A0${unit}`;
              return (
                <span key={it} style={{ display: "contents" }}>
                  <td className="border border-border p-2 text-center tabular-nums">{qDisplay}{qUnit}</td>
                  {!qtyOnly && (<td className="border border-border p-2 text-center tabular-nums">{rs(tot.amount)}</td>)}
                </span>
              );
            })}
            <td className="border border-border p-2 text-center tabular-nums">{Math.round(totals.ladooPieces)}</td>
            <td className="border border-border p-2 text-center tabular-nums">{rs(totals.ladooMakingCharge)}</td>
            <td className="border border-border p-2 text-center tabular-nums">{rs(totals.dayCost)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );

  // Slice rows into two halves for the two-page printable version.
  const splitIndex = Math.ceil(rows.length / 2);
  const slice1 = rows.slice(0, splitIndex);
  const slice2 = rows.slice(splitIndex);

  // Repeatable school heading for each printed page.
  const printHeader = (
    <div className="print-only-header" data-testid="consumption-print-header">
      <div className="ph-school">{active?.schoolName || "—"}</div>
      <div className="ph-meta">
        UDISE: <b>{active?.udiseCode || "—"}</b> · District: <b>{active?.district || "—"}</b> · Block: <b>{active?.block || "—"}</b>
      </div>
      <div className="ph-title">Daily Consumption Report</div>
      <div className="ph-meta">
        <b>{monthLabel} {year}</b> · Class Category: <b>{classLabel}</b> · Days: <b>{rows.length}</b>
      </div>
    </div>
  );

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />

      {/* Mobile-only (<md) layout — compact card list. */}
      <MobileDailyConsumption
        active={active}
        year={year} month={month} classKey={classKey}
        setYear={setYear} setMonth={setMonth} setClassKey={setClassKey}
        yearChoices={yearChoices}
        MONTHS={MONTHS}
        CLASS_OPTIONS={CLASS_OPTIONS}
        rows={rows}
        totals={totals}
        activeItems={activeItems}
        monthLabel={monthLabel}
        classLabel={classLabel}
        openingRice={openingRice}
        stockSeries={stockSeries}
        downloadCsv={downloadCsv}
        doPrint={doPrint}
      />

      <div className="consumption-page mx-auto hidden max-w-[1400px] px-4 py-10 sm:px-6 md:block lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-ink-700">Reports</p>
            <h1 className="mt-3 font-heading text-4xl font-semibold tracking-tight text-ink-900 sm:text-5xl">
              <Utensils className="mr-2 inline h-9 w-9 text-clay" /> Daily Consumption Report
            </h1>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink-700">
              Day-wise meal & ingredient ledger drawn from your saved Daily Entries × the deterministic PM POSHAN meal plan. Numbers reconcile exactly with Fund Management consumed and Annexure-1 grand totals.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 no-print">
            <Button variant="outline" onClick={downloadCsv} data-testid="consumption-csv-btn"><FileDown className="mr-1 h-4 w-4" /> CSV</Button>
            <Button variant="outline" onClick={doPrint} data-testid="consumption-print-btn"><Printer className="mr-1 h-4 w-4" /> Print</Button>
          </div>
        </div>

        {/* Filters */}
        <section className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-ink-700">Year</label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="mt-2" data-testid="consumption-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{yearChoices.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-ink-700">Month</label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="mt-2" data-testid="consumption-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m) => (<SelectItem key={m.v} value={String(m.v)}>{m.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-ink-700">Class Category</label>
              <Select value={classKey} onValueChange={setClassKey}>
                <SelectTrigger className="mt-2" data-testid="consumption-class-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASS_OPTIONS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
          <p className="mt-3 text-xs text-ink-700">
            Showing <b>{classLabel}</b> · {monthLabel} {year} · <b>{rows.length}</b> day{rows.length === 1 ? "" : "s"} of saved entries.
            {classKey === "all" ? " Quantity columns sum across every class for the day." : ` Quantity columns are this class's portion only.`}
          </p>
        </section>

        {/* Print bundle: only this block (header + table) renders when printing. */}
        <div className="consumption-print-bundle">
          {/* ON-SCREEN single table — also shown in print as a fallback, but
              actually hidden by `.consumption-screen-only` print CSS to avoid
              duplicating with the two-page paginated copy below. */}
          <section className="consumption-screen-only consumption-print-area overflow-x-auto rounded-2xl border border-border bg-white shadow-sm" data-testid="consumption-table-section">
            {renderTable(rows, 0, true)}
          </section>

          {/* PRINT-ONLY: two-page paginated copy. Each page repeats the school
              heading + thead. Page 1 holds the first half of days; page 2 holds
              the rest + the TOTAL row + signatures. Hidden on screen. */}
          <div className="consumption-print-two-pager" aria-hidden>
            <section className="consumption-print-page" data-testid="consumption-print-page-1">
              {printHeader}
              <div className="consumption-print-area">
                {renderTable(slice1, 0, false)}
              </div>
              <div className="consumption-print-page-label">Page 1 of 2</div>
            </section>
            <section className="consumption-print-page" data-testid="consumption-print-page-2">
              {printHeader}
              <div className="consumption-print-area">
                {renderTable(slice2, splitIndex, true)}
              </div>
              {/* Signatures appear on the final page only. */}
              <div className="consumption-print-signatures">
                <div className="sig-cell">
                  <div className="sig-line" />
                  <div className="sig-label">Signature of SMC Chairperson</div>
                </div>
                <div className="sig-cell">
                  <div className="sig-line" />
                  <div className="sig-label">Signature of Headmaster</div>
                </div>
              </div>
              <div className="consumption-print-page-label">Page 2 of 2</div>
            </section>
          </div>
        </div>{/* /consumption-print-bundle */}

        <footer className="mt-6 text-xs text-ink-700">
          Numbers come from each day's deterministic PM POSHAN meal-plan breakdown × that day's saved attendance — same source feeding Fund Management consumed and Annexure-1 grand total. Rice stock cascades from the previous month's closing rice (Fund Management).
        </footer>
      </div>
    </main>
  );
}
